/**
 * Taxas de mercado para o PDF comparativo, direto do Banco Central (SGS).
 *
 * A API do SGS é pública e responde com `Access-Control-Allow-Origin: *`, então
 * o navegador busca sem passar pelo nosso backend. Séries usadas:
 *
 *   4389  CDI anualizado (base 252), % a.a.                      — diária
 *   20749 Juros médios PF, aquisição de veículos, % a.a.          — mensal
 *   20772 Juros médios PF, financiamento imobiliário a taxas de
 *         mercado, % a.a.                                         — mensal
 *   192   INCC (FGV), % no mês — acumulamos os últimos 12          — mensal
 *   13522 IPCA acumulado em 12 meses, %                           — mensal
 *   1178  Selic anualizada (base 252), % a.a.                     — diária
 *   189   IGP-M (FGV), % no mês — acumulamos os últimos 12        — mensal
 *   21340 IVG-R (valorização de imóveis residenciais), número-
 *         índice — variação de 12 meses                           — mensal
 *   195   Poupança, rentabilidade no mês, %                       — diária
 *   1     Dólar comercial (PTAX venda), R$                        — diária
 *
 * Se o BCB não responder (fora do ar, lento), o PDF sai com os valores de
 * PADRAO e diz isso nas premissas — melhor um número datado e declarado do que
 * travar a proposta do vendedor na frente do cliente.
 */

export type Indicador = {
  valor: number;
  data: string;
  /** 'padrao' quando esta série caiu no valor de referência. */
  fonte?: 'bcb' | 'padrao';
};

export type Indicadores = {
  cdiAa: Indicador;
  jurosVeiculoAa: Indicador;
  jurosImovelAa: Indicador;
  incc12m: Indicador;
  ipca12m: Indicador;
  selicAa: Indicador;
  igpm12m: Indicador;
  /** Valorização de imóveis residenciais em 12 meses (IVG-R), %. */
  ivgr12m: Indicador;
  poupancaAm: Indicador;
  dolar: Indicador;
  /** 'bcb' quando todas vieram da API; 'padrao' quando alguma caiu no fallback. */
  fonte: 'bcb' | 'padrao';
};

/** Últimos valores conferidos no SGS em 28/09/2026. */
export const PADRAO: Omit<Indicadores, 'fonte'> = {
  cdiAa: { valor: 13.65, data: '25/09/2026' },
  jurosVeiculoAa: { valor: 26.52, data: '01/07/2026' },
  jurosImovelAa: { valor: 14.28, data: '01/07/2026' },
  incc12m: { valor: 6.61, data: '01/08/2026' },
  ipca12m: { valor: 4.22, data: '01/08/2026' },
  // Conferidos no SGS em 16/10/2026.
  selicAa: { valor: 13.65, data: '16/10/2026' },
  igpm12m: { valor: 3.35, data: '01/09/2026' },
  ivgr12m: { valor: 5.28, data: '01/07/2026' },
  poupancaAm: { valor: 0.6578, data: '15/10/2026' },
  dolar: { valor: 5.0826, data: '16/10/2026' },
};

const TIMEOUT_MS = 6000;

type PontoSgs = { data: string; valor: string };

const buscarSerie = async (codigo: number, ultimos: number): Promise<PontoSgs[]> => {
  const ctrl = new AbortController();
  const timer = setTimeout(() => ctrl.abort(), TIMEOUT_MS);
  try {
    const resp = await fetch(
      `https://api.bcb.gov.br/dados/serie/bcdata.sgs.${codigo}/dados/ultimos/${ultimos}?formato=json`,
      { signal: ctrl.signal },
    );
    if (!resp.ok) throw new Error(`SGS ${codigo}: HTTP ${resp.status}`);
    const dados = (await resp.json()) as PontoSgs[];
    if (!Array.isArray(dados) || dados.length === 0) throw new Error(`SGS ${codigo}: vazio`);
    return dados;
  } finally {
    clearTimeout(timer);
  }
};

const ultimoValor = async (codigo: number): Promise<Indicador> => {
  const dados = await buscarSerie(codigo, 1);
  const ponto = dados[dados.length - 1];
  const valor = Number(ponto.valor);
  if (!Number.isFinite(valor)) throw new Error(`SGS ${codigo}: valor inválido`);
  return { valor, data: ponto.data };
};

/** Acumula 12 variações mensais: Π(1 + v/100) − 1. */
const acumulado12m = async (codigo: number): Promise<Indicador> => {
  const dados = await buscarSerie(codigo, 12);
  if (dados.length < 12) throw new Error(`SGS ${codigo}: menos de 12 meses`);
  const fator = dados.reduce((acc, p) => acc * (1 + Number(p.valor) / 100), 1);
  if (!Number.isFinite(fator)) throw new Error(`SGS ${codigo}: valor inválido`);
  return { valor: (fator - 1) * 100, data: dados[dados.length - 1].data };
};

/** Variação de 12 meses de um número-índice: último / o de 12 meses antes − 1. */
const variacao12m = async (codigo: number): Promise<Indicador> => {
  const dados = await buscarSerie(codigo, 13);
  if (dados.length < 13) throw new Error(`SGS ${codigo}: menos de 13 pontos`);
  const fim = Number(dados[dados.length - 1].valor);
  const inicio = Number(dados[0].valor);
  if (!Number.isFinite(fim) || !Number.isFinite(inicio) || inicio <= 0) throw new Error(`SGS ${codigo}: valor inválido`);
  return { valor: (fim / inicio - 1) * 100, data: dados[dados.length - 1].data };
};

let cache: { em: number; valor: Indicadores } | null = null;
const CACHE_MS = 60 * 60 * 1000;

export const carregarIndicadores = async (): Promise<Indicadores> => {
  if (cache && Date.now() - cache.em < CACHE_MS) return cache.valor;

  const tentativas = await Promise.allSettled([
    ultimoValor(4389),
    ultimoValor(20749),
    ultimoValor(20772),
    acumulado12m(192),
    ultimoValor(13522),
    ultimoValor(1178),
    acumulado12m(189),
    variacao12m(21340),
    ultimoValor(195),
    ultimoValor(1),
  ]);
  const pegar = (i: number, padrao: Indicador): Indicador => {
    const t = tentativas[i];
    return t.status === 'fulfilled' ? { ...t.value, fonte: 'bcb' } : { ...padrao, fonte: 'padrao' };
  };

  const valor: Indicadores = {
    cdiAa: pegar(0, PADRAO.cdiAa),
    jurosVeiculoAa: pegar(1, PADRAO.jurosVeiculoAa),
    jurosImovelAa: pegar(2, PADRAO.jurosImovelAa),
    incc12m: pegar(3, PADRAO.incc12m),
    ipca12m: pegar(4, PADRAO.ipca12m),
    selicAa: pegar(5, PADRAO.selicAa),
    igpm12m: pegar(6, PADRAO.igpm12m),
    ivgr12m: pegar(7, PADRAO.ivgr12m),
    poupancaAm: pegar(8, PADRAO.poupancaAm),
    dolar: pegar(9, PADRAO.dolar),
    fonte: tentativas.every((t) => t.status === 'fulfilled') ? 'bcb' : 'padrao',
  };
  // Só guarda o resultado completo: um fallback não deve grudar por uma hora.
  if (valor.fonte === 'bcb') cache = { em: Date.now(), valor };
  return valor;
};
